/**
 * Stable close contract for logical (room-scoped) authorization transitions.
 *
 * The server writes these strings into an in-band Hocuspocus CLOSE message and
 * the frontend provider classifies an authorization close by matching them, so
 * they must stay byte-identical across releases. They live here rather than in
 * either tier because both tiers must read the *same* literal: a duplicated
 * table would drift silently and a drifted client would read a revocation as a
 * network blip.
 *
 * They are deliberately opaque - no identifier, no ACL detail and nothing that
 * distinguishes one principal from another.
 */
export const COLLABORATION_CLOSE_REASONS = {
  /** This room's own credential or ACL check failed. */
  authorizationDenied: "workith.auth.denied",
  /** Editable access became read-only for this principal on the parent document. */
  authorizationDowngraded: "workith.auth.downgraded",
  /** Parent document/principal access was revoked; propagated to every room. */
  authorizationRevoked: "workith.auth.revoked",
  /** No accepted renewal arrived before the hard lease deadline. */
  leaseExpired: "workith.auth.lease-expired",
  /** Room-local: the section no longer belongs to its parent document. */
  sectionUnavailable: "workith.auth.section-unavailable",
  /** Too many logical rooms are open for this document and principal. */
  roomLimitExceeded: "workith.auth.room-limit-exceeded",
} as const;

export type CollaborationCloseReason =
  (typeof COLLABORATION_CLOSE_REASONS)[keyof typeof COLLABORATION_CLOSE_REASONS];

/** Policy violation: the only close code the collaboration transport uses. */
export const COLLABORATION_POLICY_CLOSE_CODE = 1008;

/** The literal credential a public read-only room connects with. */
export const ANONYMOUS_COLLABORATION_TOKEN = "anonymous";
